// Tree row mapping (Phase 3b) — PURE. Turns a node's scoped /breakdown response
// (rows at ONE level, already per-(entity, currency) from the server) into sorted
// row views for that level. Unlike the Flat rollup, this does NOT merge across
// currencies: each fetched row stays its own row (a node that legitimately spans
// currencies renders one row per currency — §6). Each row's metrics is its own
// authoritative single-currency aggregate (true per-level totals).

import type { ReportingBreakdownRow, ReportingDimension } from '../../types/reporting';
import { dimId, dimName } from './breakdownColumns';
import {
  accumulateMetrics,
  rowAttributes,
  sortRowViews,
  type BreakdownRowView,
  type RollupSort,
} from './breakdownGrouping';

// A tree row = a row view (dim/id/name/adAccountId/metrics) plus its native
// currency (part of the node's identity, so per-currency siblings stay distinct).
export interface TreeRowData extends BreakdownRowView {
  currency: string;
}

export function toTreeRows(
  rows: readonly ReportingBreakdownRow[],
  dim: ReportingDimension,
  sort: RollupSort,
): TreeRowData[] {
  const views: TreeRowData[] = rows.map((row) => ({
    dim,
    id: dimId(row, dim),
    name: dimName(row, dim),
    adAccountId: row.adAccountId,
    // One-row aggregate → single-currency metrics (ratios recomputed from its sums).
    metrics: accumulateMetrics([row]),
    // Every Tree node is fetched at its own grain → carries its entity attributes.
    attributes: rowAttributes(row),
    // Per-level status: the node's own-level status is in its slot; nameStatus
    // reads the field matching the node's dim (extra ancestor fields go unused).
    campaignStatus: row.campaignStatus,
    adsetStatus: row.adsetStatus,
    adStatus: row.adStatus,
    // Server populates creative only on ad-grain fetches (groupBy=['ad']); a
    // coarser node's fetch carries null. Pass through — the ad node gets its
    // creative, every other level gets null.
    creative: row.creative,
    currency: row.currency,
  }));
  return sortRowViews(views, sort);
}
